<template>
  <figure class="wrapper m-d">
    <div
      id="carrot-surfers"
      class="advergaming-minigame carrot-surfers"
      ref="rootRef"
    >
      <Counter :score="score" />

      <Transition @leave="onOverlayLeave">
        <GameOverlay v-if="!hideOverlay" mobile="none" desktop="keys" />
      </Transition>
    </div>

    <div class="advergaming-buttons">
      <button
        class="advergaming-buttons__button--left"
        @click="onClick('ArrowLeft')"
      >
        <div class="advergaming-buttons__button__inner">
          <SvgSprite
            symbol="ui-advergaming-overlay-arrow-key"
            size="19 21"
            class="advergaming-buttons__button__arrow"
          />
        </div>
      </button>
      <button
        class="advergaming-buttons__button--right"
        @click="onClick('ArrowRight')"
      >
        <div class="advergaming-buttons__button__inner">
          <SvgSprite
            symbol="ui-advergaming-overlay-arrow-key"
            size="19 21"
            class="advergaming-buttons__button__arrow"
          />
        </div>
      </button>
    </div>

    <Caption :content="content" />
  </figure>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, PropType, ref } from 'vue'
import { useStore } from '@nanostores/vue'

import Counter from '@/components/advergaming/Counter.vue'
import Caption from '@/components/advergaming/Caption.vue'
import { AdvergamingCaption } from '@/components/advergaming/Caption.vue'
import { CarrotSurfers } from './CarrotSurfers'
import { useSwipe, SwipeDirection } from '@vueuse/core'
import { useAdvergamingStore } from '@/stores/advergaming'
import GameOverlay from '@/components/advergaming/GameOverlay.vue'
import gsap from 'gsap'

defineProps({
  content: {
    type: Object as PropType<AdvergamingCaption>,
    required: true,
  },
})

const rootRef = ref()

const game = new CarrotSurfers({
  id: 'carrot-surfers',
  store: useAdvergamingStore(),
})

const hideOverlay = useStore(game.store.$interactedWith)
const onOverlayLeave = (el: Element, done: () => void) => {
  gsap.to(el, {
    opacity: 0,
    scale: 1.1,
    duration: 0.15,
    onComplete: done,
  })
}

const score = useStore(game.store.$score)

onMounted(() => {
  game.init({
    container: rootRef.value,
    renderer: {
      antialias: true,
    },
  })
})

onBeforeUnmount(() => {
  game.dispose()
})

useSwipe(rootRef, {
  onSwipeEnd(_e: TouchEvent, direction: SwipeDirection) {
    switch (direction) {
      case SwipeDirection.LEFT:
        onClick('ArrowLeft')
        break
      case SwipeDirection.RIGHT:
        onClick('ArrowRight')
        break
      default:
        break
    }
  },
})

const onClick = (keyCode: 'ArrowLeft' | 'ArrowRight') => {
  if (!game) {
    return
  }

  game.keys[keyCode].isDown = true
  game.keys[keyCode].isJustDown = true
  game.store.$interactedWith.set(true)
}
</script>

<style lang="scss" scoped>
.carrot-surfers {
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 20rem;
    background-image: linear-gradient(var(--c-background), transparent);
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 10rem;
    background-image: linear-gradient(transparent, var(--c-background));
  }

  @include mq($until: m) {
    /* stylelint-disable declaration-no-important */
    height: auto !important;
    aspect-ratio: 3/4 !important;
    /* stylelint-enable declaration-no-important */
  }
}
</style>
